// index_with_532.js (React 17, let Player own Provider/PersistGate/Router)

import React from 'react';
import ReactDOM from 'react-dom';
import ErrorBoundary from './ErrorBoundary';

// Use the vendored Player (has Provider + PersistGate + HashRouter)
import Player from './vendor/player-application/Player';

import choirConfig from './choirPlayerConfig';

// diagnostics for LS/IDB blobs
import localforage from 'localforage';

// CRA SW helper (optional)
import * as serviceWorkerRegistration from './serviceWorkerRegistration';

// pgw 01-oct-2025
import { logToIndexedDB } from './indexedLogger';

// pgw 30-sep-2025 - replace all debugging debugLog calls with debugLog
import { debugLog } from './debugLogIDB';

const PERSIST_KEY = 'persist:root';



function truncate(str, max = 240) {
  if (str == null) return String(str);
  const s = String(str);
  return s.length > max ? s.slice(0, max) + '…(truncated)' : s;
}

async function readBlobs() {
  let lsBlob = null;
  try { lsBlob = window.localStorage.getItem(PERSIST_KEY); } catch (e) {
    console.warn('[diag] Could not read LocalStorage:', e);
  }

  let idbBlob = null;
  try {
    const lf = localforage.createInstance({ name: 'ChoirPlayer', storeName: 'persistRoot' });
    await lf.ready();
    idbBlob = await lf.getItem(PERSIST_KEY);
  } catch (e) {
    console.warn('[diag] Could not read IDB via localforage:', e);
  }

  return { lsBlob, idbBlob };
}

async function logPersistBlobs(tag = 'SNAPSHOT') {
  const { lsBlob, idbBlob } = await readBlobs();
  const backend = (typeof window !== 'undefined' && window.__STORE_BACKEND__) || '(unknown)';
  debugLog(`[diag ${tag}] Hydration backend in use:`, backend);
  debugLog(`[diag ${tag}] LocalStorage ${PERSIST_KEY} =`, truncate(lsBlob));
  debugLog(`[diag ${tag}] IndexedDB   ${PERSIST_KEY} =`, truncate(idbBlob));
}

// Global error logging
window.addEventListener('error', (e) => {
  console.error('[global onerror]', e.message, e.error);
});
window.addEventListener('unhandledrejection', (e) => {
  console.error('[global unhandledrejection]', e.reason);
});

// Runtime config
const runtimeConfig = (typeof window !== 'undefined' && window.__RUNTIME_CONFIG__) || {};

function bootstrapApp(clientConfig = {}) {
  // Pre-hydration snapshot (non-blocking)
  logPersistBlobs('BEFORE');

  ReactDOM.render(
    <React.StrictMode>
      <ErrorBoundary>
        {/* Player already includes Provider + PersistGate + HashRouter */}
        <Player clientConfig={clientConfig} />
      </ErrorBoundary>
    </React.StrictMode>,
    document.getElementById('root')
  );

  // Optional SW registration after render
  if (serviceWorkerRegistration && typeof serviceWorkerRegistration.register === 'function') {
    debugLog('[SW] registering…');
    serviceWorkerRegistration.register({
      onSuccess: (registration) => { debugLog('[SW] Registration successful:', registration); },
      onUpdate: (registration)  => { debugLog('[SW] Update found:', registration); },
    });

    // SW → Page log bridge
	debugLog('[SW] after serviceWorkerRegistration.register navigator.serviceWorker = ', navigator.serviceWorker);



	if (navigator.serviceWorker) {
	  navigator.serviceWorker.addEventListener('message', e => {
		if (e.data?.type === 'SW_LOG') {
		  console.log('[SW → Page]', ...e.data.payload);
		}
	  });
	}


  }
}

bootstrapApp(choirConfig);

// --- pgw add: connectivity + VPN suspicion watcher (non-breaking) ---
(function(){
  async function pingSW(timeoutMs = 1500) {
    try {
      if (!navigator.serviceWorker || !navigator.serviceWorker.controller) return { ok:false, reason:'no-controller' };
      const { port1, port2 } = new MessageChannel();
      const r = new Promise((resolve) => {
        const t = setTimeout(() => { try { port1.onmessage = null; } catch(_){}; resolve({ ok:false, reason:'timeout' }); }, timeoutMs);
        port1.onmessage = (e) => { clearTimeout(t); resolve({ ok:true, data:e.data }); };
      });
      navigator.serviceWorker.controller.postMessage({ type: 'PING' }, [port2]);
      return await r;
    } catch (e) {
      return { ok:false, reason:String(e) };
    }
  }

  async function head(url, timeoutMs=1500) {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort('timeout'), timeoutMs);
    try {
      await fetch(url, { method:'HEAD', cache:'no-store', credentials:'omit', signal: ctrl.signal, mode:'no-cors' });
      return true;
    } catch (e) {
      return false;
    } finally {
      clearTimeout(t);
    }
  }

  async function probeConnectivity() {
    const online = navigator.onLine;
    const sw = await pingSW(1200);
    const sameOriginOk = await head('/_network-check?t=' + Date.now(), 1500);
    const offOriginOk = await head('https://www.cloudflare.com/cdn-cgi/trace', 1800);

    const suspect = online && (!sw.ok || (!sameOriginOk && !offOriginOk) || (sameOriginOk && !offOriginOk));
    const detail = { online, sw: sw.ok, sameOriginOk, offOriginOk, suspect };

    try {
      window.__CONNECTIVITY_STATUS__ = detail;
      window.dispatchEvent(new CustomEvent('connectivity-change', { detail }));
      if (navigator.serviceWorker && navigator.serviceWorker.controller) {
        const { port1, port2 } = new MessageChannel();
        navigator.serviceWorker.controller.postMessage({ type:'IS_VPN_SUSPECT', suspect }, [port2]);
      }
    } catch (_) {}
    if (typeof console !== 'undefined' && console.log) {
      console.log('[connectivity]', detail);
    }
    return detail;
  }

  try {
    probeConnectivity();
    window.addEventListener('online', () => probeConnectivity());
    window.addEventListener('offline', () => probeConnectivity());
    setTimeout(probeConnectivity, 3000);
    setTimeout(probeConnectivity, 8000);
  } catch (_) {}
})();
// --- end add ---
